import { assign, createActor, fromPromise, setup } from "xstate5";
import { type AppRouterOutputs, trpc } from "../trpc/client";

export type InstitutionConfig = AppRouterOutputs["institutionConfig"]["get"];

type Context = {
	error?: string;
	booklistConfig: InstitutionConfig;
	enabledAccessLength: boolean;
	isEAorIA: boolean;
	isIA: boolean;
	isEA: boolean;
	isAccessProgramEnabled: boolean;
	isRequirementStatusEnabled: boolean;
};

type Events =
	| {
			type: "retry";
	  }
	| {
			type: "done.invoke.fetch";
			data: Context["booklistConfig"];
	  }
	| {
			type: "error.platform.fetch";
			data: Error;
	  };

export const institutionStateMachine = setup({
	types: {
		context: {} as Context,
		events: {} as Events,
	},
	actors: {
		fetch: fromPromise(() => trpc.institutionConfig.get.query()),
	},
	actions: {
		setBooklistConfig: assign({
			booklistConfig: (_, params: Context["booklistConfig"]) => params,
		}),
		setEnabledAccessLength: assign({
			enabledAccessLength: (_, params: Context["enabledAccessLength"]) =>
				params,
		}),
		setIsAccessProgramEnabled: assign({
			isAccessProgramEnabled: (_, params: Context["isAccessProgramEnabled"]) =>
				params,
		}),
		setIsRequirementStatusEnabled: assign({
			isRequirementStatusEnabled: (
				_,
				params: Context["isRequirementStatusEnabled"],
			) => params,
		}),
		setIsEAorIA: assign({
			isEAorIA: (_, params: Context["isEAorIA"]) => params,
		}),
		setIsIA: assign({
			isIA: (_, params: Context["isIA"]) => params,
		}),
		setIsEA: assign({
			isEA: (_, params: Context["isEA"]) => params,
		}),
		setError: assign({
			error: (_, params: Error) => params.message,
		}),
	},
}).createMachine({
	id: "getInstitutionConfig",
	context: {
		booklistConfig: {} as InstitutionConfig,
		enabledAccessLength: false,
		isEAorIA: false,
		isIA: false,
		isEA: false,
		isAccessProgramEnabled: false,
		isRequirementStatusEnabled: false,
	},
	initial: "loading",
	states: {
		loading: {
			invoke: {
				id: "fetch",
				src: "fetch",
				onDone: {
					target: "success",
					actions: [
						{
							type: "setBooklistConfig",
							params: ({ event }) => event.output,
						},
						{
							type: "setEnabledAccessLength",
							params: ({ event }) => event.output.enabledAccessLength,
						},
						{
							type: "setIsEAorIA",
							params: ({ event }) => event.output.isEAorIA,
						},
						{
							type: "setIsIA",
							params: ({ event }) => event.output.isIA,
						},
						{
							type: "setIsEA",
							params: ({ event }) => event.output.isEA,
						},
						{
							type: "setIsAccessProgramEnabled",
							params: ({ event }) => event.output.isAccessProgramEnabled,
						},
						{
							type: "setIsRequirementStatusEnabled",
							params: ({ event }) => event.output.isRequirementStatusEnabled,
						},
					],
				},
				onError: {
					target: "error",
					actions: {
						type: "setError",
						params: ({ event }) => event.error as Error,
					},
				},
			},
		},
		success: {
			type: "final",
		},
		error: {
			on: {
				retry: "loading",
			},
		},
	},
});

export const institutionStateActor = createActor(
	institutionStateMachine,
).start();
